import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  secondaryActionButtonSx,
  secondarySectionFrameSx,
  secondarySectionHeaderSx,
} from '../../styles/secondaryMsiStyles';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { uppercaseLabel } from '../../styles/typography';
import {
  fetchAdminFactorDataInventory,
  fetchFactorLoaderRunHistory,
} from '../../utils/ApiCall';

const INVENTORY_DAYS = 90;
// A run row shows the server's outcome: FDL's go-live verdict for a loaded
// deployment run, otherwise the load status.
const RUN_OUTCOME_CHIP = {
  success: { label: 'success', color: 'success' },
  waiting_for_msi: { label: 'Waiting for MSI', color: 'warning' },
  failed: { label: 'failed', color: 'error' },
};
const RUN_STATUS_HINT = 'For a deployment run, FDL’s go-live verdict when the run ended. '
  + 'Factor Data > Deployments shows the current state.';
const LOADER_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'barra', label: 'Barra' },
  { value: 'axioma', label: 'Axioma' },
];

const formatTimestamp = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const formatAsOfDate = (value) => {
  if (!value) return '-';
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

const formatModelHorizon = (model, horizon) => {
  const modelText = String(model || '').trim();
  const horizonText = String(horizon || '').trim();
  if (modelText && horizonText) {
    return `${modelText} / ${horizonText}`;
  }
  return modelText || horizonText || '-';
};

const toNonNegativeInt = (value, fallback = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return fallback;
  }
  return Math.floor(numeric);
};

const formatCount = (value) => toNonNegativeInt(value).toLocaleString();

const toRunSortKey = (item) => {
  const createdAt = Date.parse(String(item?.created_at || ''));
  if (!Number.isNaN(createdAt)) {
    return createdAt;
  }
  const finishedAt = Date.parse(String(item?.finished_at || ''));
  if (!Number.isNaN(finishedAt)) {
    return finishedAt;
  }
  const id = Number(item?.id || 0);
  if (Number.isFinite(id)) {
    return id;
  }
  return 0;
};

const collapseLoaderRunsForDisplay = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }
  const latestByScope = new Map();
  items.forEach((item) => {
    const loader = String(item?.loader || '').trim().toLowerCase();
    const model = String(item?.model || '').trim().toUpperCase();
    const horizon = String(item?.horizon || '').trim().toUpperCase();
    const asOf = formatAsOfDate(item?.as_of_date);
    const scopeKey = `${loader}|${model}|${horizon}|${asOf}`;
    const current = latestByScope.get(scopeKey);
    if (!current || toRunSortKey(item) > toRunSortKey(current)) {
      latestByScope.set(scopeKey, item);
    }
  });
  return Array.from(latestByScope.values()).sort((left, right) => (
    toRunSortKey(right) - toRunSortKey(left)
  ));
};

const summarizeLoaderMessage = (item) => {
  // MSI's message about the release, as a deployment run recorded it.
  const registrationMessage = String(item?.metadata?.activation?.msi_registration?.message || '').trim();
  if (item?.outcome === 'waiting_for_msi') {
    return registrationMessage || 'MSI had not finished registering the release when the run ended.';
  }
  const status = String(item?.status || '').trim().toLowerCase();
  if (status === 'success') {
    // The load succeeded, but FDL failed the run because its release did not go live.
    return item?.outcome === 'failed'
      ? `Loaded, but not live when the run ended. ${registrationMessage}`.trim()
      : 'success';
  }
  const text = String(item?.error_text || '').trim().toLowerCase();
  if (!text) {
    return 'loader failed';
  }
  if (
    text.includes('no primary zip found')
    || text.includes('no remote identity zip found')
    || text.includes('no .zip files found')
    || text.includes('file not found')
  ) {
    return 'source files unavailable';
  }
  if (
    text.includes('authentication')
    || text.includes('access denied')
    || text.includes('permission denied')
    || text.includes('invalid credentials')
  ) {
    return 'provider authentication failed';
  }
  if (text.includes('timeout') || text.includes('timed out')) {
    return 'loader timed out';
  }
  return String(item?.error_text || '').trim() || 'loader failed';
};

const sectionFrameSx = {
  ...secondarySectionFrameSx,
};

const sectionHeaderSx = {
  px: { xs: 1.2, md: 1.5 },
  py: 1,
  display: 'flex',
  alignItems: { xs: 'flex-start', sm: 'center' },
  justifyContent: 'space-between',
  gap: 1,
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: (theme) => alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.16 : 0.42),
  ...secondarySectionHeaderSx,
};

const controlRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  flexWrap: 'wrap',
};

const refreshButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  minHeight: 30,
  ...secondaryActionButtonSx,
};

function FactorDataMonitoringPanel({ active = false }) {
  const [loaderHistoryLoading, setLoaderHistoryLoading] = useState(false);
  const [loaderHistoryLoaded, setLoaderHistoryLoaded] = useState(false);
  const [loaderHistoryError, setLoaderHistoryError] = useState('');
  const [loaderHistoryItems, setLoaderHistoryItems] = useState([]);
  const [loaderHistorySources, setLoaderHistorySources] = useState({
    barra: true,
    axioma: true,
  });
  const [loaderHistoryFailedOnly, setLoaderHistoryFailedOnly] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [inventoryError, setInventoryError] = useState('');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryReason, setInventoryReason] = useState('');
  const [inventoryLoader, setInventoryLoader] = useState('all');
  const [inventoryMeta, setInventoryMeta] = useState({
    returnedRowCount: 0,
    hasMore: false,
    limit: null,
  });

  const loadLoaderHistory = useCallback(async ({
    failedOnly = loaderHistoryFailedOnly,
  } = {}) => {
    setLoaderHistoryLoading(true);
    setLoaderHistoryError('');
    try {
      const response = await fetchFactorLoaderRunHistory({
        status: failedOnly ? 'failed' : undefined,
        limit: 100,
      });
      if (!response || response.success === false) {
        throw new Error(response?.message || 'Failed to fetch factor loader history.');
      }
      setLoaderHistoryItems(Array.isArray(response.items) ? response.items : []);
    } catch (error) {
      setLoaderHistoryError(error?.message || 'Failed to fetch factor loader history.');
      setLoaderHistoryItems([]);
    } finally {
      setLoaderHistoryLoaded(true);
      setLoaderHistoryLoading(false);
    }
  }, [loaderHistoryFailedOnly]);

  const loadInventory = useCallback(async ({
    loader = inventoryLoader,
  } = {}) => {
    setInventoryLoading(true);
    setInventoryError('');
    setInventoryReason('');
    try {
      const response = await fetchAdminFactorDataInventory({
        loader,
        days: INVENTORY_DAYS,
      });
      if (!response || response.success === false) {
        throw new Error(response?.message || 'Failed to fetch factor inventory.');
      }
      const items = Array.isArray(response.items) ? response.items : [];
      const returnedRowCount = toNonNegativeInt(response.returned_row_count, items.length);
      const limit = response.limit === undefined || response.limit === null
        ? null
        : toNonNegativeInt(response.limit, returnedRowCount);
      setInventoryItems(items);
      setInventoryReason(response.reason || '');
      setInventoryMeta({
        returnedRowCount,
        hasMore: Boolean(response.has_more),
        limit,
      });
    } catch (error) {
      setInventoryError(error?.message || 'Failed to fetch factor inventory.');
      setInventoryItems([]);
      setInventoryMeta({
        returnedRowCount: 0,
        hasMore: false,
        limit: null,
      });
    } finally {
      setInventoryLoaded(true);
      setInventoryLoading(false);
    }
  }, [inventoryLoader]);

  useEffect(() => {
    if (!active) {
      return;
    }
    if (!loaderHistoryLoaded && !loaderHistoryLoading) {
      loadLoaderHistory();
    }
    if (!inventoryLoaded && !inventoryLoading) {
      loadInventory();
    }
  }, [
    active,
    inventoryLoaded,
    inventoryLoading,
    loadInventory,
    loadLoaderHistory,
    loaderHistoryLoaded,
    loaderHistoryLoading,
  ]);

  const loaderHistoryVisibleItems = useMemo(() => (
    collapseLoaderRunsForDisplay(loaderHistoryItems).filter((item) => {
      const key = String(item?.loader || '').trim().toLowerCase();
      return Boolean(loaderHistorySources[key]);
    })
  ), [loaderHistoryItems, loaderHistorySources]);

  // The server's run outcome also fails a loaded release that FDL reports did not go live.
  const failedRunCount = useMemo(() => (
    loaderHistoryItems.filter((item) => item?.outcome === 'failed').length
  ), [loaderHistoryItems]);

  const inventoryCountLabel = inventoryMeta.hasMore
    ? `Showing first ${formatCount(inventoryMeta.returnedRowCount)} inventory rows`
    : `${formatCount(inventoryMeta.returnedRowCount || inventoryItems.length)} inventory rows`;

  const handleLoaderHistorySourceToggle = (source) => {
    const normalizedSource = source === 'axioma' ? 'axioma' : 'barra';
    setLoaderHistorySources((current) => ({
      ...current,
      [normalizedSource]: !current[normalizedSource],
    }));
  };

  const handleLoaderHistoryFailedOnlyChange = async (event) => {
    const nextValue = Boolean(event.target.checked);
    setLoaderHistoryFailedOnly(nextValue);
    await loadLoaderHistory({ failedOnly: nextValue });
  };

  const handleInventoryLoaderChange = async (nextLoader) => {
    setInventoryLoader(nextLoader);
    await loadInventory({ loader: nextLoader });
  };

  const renderRunHistoryTable = () => {
    if (loaderHistoryLoading) {
      return (
        <Box sx={{ py: 5, display: 'grid', placeItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="caption" color="text.secondary">
            Loading run history...
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Box} sx={{ maxHeight: 340 }}>
        <Table stickyHeader size="small" aria-label="Loader run history">
          <TableHead>
            <TableRow>
              <TableCell title={RUN_STATUS_HINT}>Status</TableCell>
              <TableCell>Loader</TableCell>
              <TableCell>As-of date</TableCell>
              <TableCell>Model / horizon</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Finished</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderHistoryVisibleItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No run history rows found for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {loaderHistoryVisibleItems.map((item) => {
              const outcomeChip = RUN_OUTCOME_CHIP[item.outcome] || { label: item.outcome || 'unknown', color: 'default' };
              const messageText = summarizeLoaderMessage(item);
              return (
                <TableRow key={item.id || `${item.loader}-${item.model}-${item.as_of_date}-${item.created_at}`}>
                  <TableCell>
                    <Chip
                      size="small"
                      color={outcomeChip.color}
                      label={outcomeChip.label}
                      sx={uppercaseLabel()}
                    />
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {item.loader || '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatAsOfDate(item.as_of_date)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                    {formatModelHorizon(item.model, item.horizon)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatTimestamp(item.created_at)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatTimestamp(item.finished_at)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography
                      variant="caption"
                      color={item.outcome === 'failed' ? 'error.main' : 'text.secondary'}
                      sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={messageText}
                    >
                      {messageText}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderInventoryTable = () => {
    if (inventoryLoading) {
      return (
        <Box sx={{ py: 5, display: 'grid', placeItems: 'center', gap: 1 }}>
          <CircularProgress size={22} />
          <Typography variant="caption" color="text.secondary">
            Loading inventory...
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Box} sx={{ maxHeight: 340 }}>
        <Table stickyHeader size="small" aria-label="Factor inventory">
          <TableHead>
            <TableRow>
              <TableCell>Loader</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Model / horizon</TableCell>
              <TableCell>Table</TableCell>
              <TableCell align="right">Rows</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inventoryItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No inventory rows found for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {inventoryItems.map((item) => (
              <TableRow key={`${item.loader}-${item.model}-${item.horizon || ''}-${item.as_of_date}-${item.table_name}`}>
                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {item.loader || '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatAsOfDate(item.as_of_date)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                  {formatModelHorizon(item.model, item.horizon)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                  {item.table_name || '-'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {Number(item.row_count || 0).toLocaleString()}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {item.last_operation || '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatTimestamp(item.updated_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Factor Data Monitoring
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Run history and inventory state for factor loaders.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Chip size="small" label={`${loaderHistoryVisibleItems.length} runs`} variant="outlined" />
          <Chip size="small" label={`${failedRunCount} failed`} color={failedRunCount ? 'error' : 'default'} variant="outlined" />
          <Chip size="small" label={inventoryCountLabel} variant="outlined" />
        </Box>
      </Box>

      <Box sx={sectionFrameSx}>
        <Box sx={sectionHeaderSx}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Loader Run History
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Latest audit rows from factor loader runs.
            </Typography>
          </Box>
          <Box sx={controlRowSx}>
            <Chip
              clickable
              disabled={loaderHistoryLoading}
              aria-label="Run history loader Barra"
              label="Barra"
              color={loaderHistorySources.barra ? 'primary' : 'default'}
              variant={loaderHistorySources.barra ? 'filled' : 'outlined'}
              onClick={() => handleLoaderHistorySourceToggle('barra')}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              clickable
              disabled={loaderHistoryLoading}
              aria-label="Run history loader Axioma"
              label="Axioma"
              color={loaderHistorySources.axioma ? 'primary' : 'default'}
              variant={loaderHistorySources.axioma ? 'filled' : 'outlined'}
              onClick={() => handleLoaderHistorySourceToggle('axioma')}
              sx={{ fontWeight: 700 }}
            />
            <FormControlLabel
              sx={{ ml: 0.25, mr: 0 }}
              control={(
                <Switch
                  checked={loaderHistoryFailedOnly}
                  onChange={handleLoaderHistoryFailedOnlyChange}
                  disabled={loaderHistoryLoading}
                  size="small"
                />
              )}
              label="Failed only"
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => loadLoaderHistory()}
              disabled={loaderHistoryLoading}
              sx={refreshButtonSx}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        {loaderHistoryError && (
          <Alert severity="error" sx={{ m: 1.2 }}>
            {loaderHistoryError}
          </Alert>
        )}
        {renderRunHistoryTable()}
      </Box>

      <Box sx={sectionFrameSx}>
        <Box sx={sectionHeaderSx}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              Factor Inventory
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Rows loaded in the last {INVENTORY_DAYS} days. Loaded data goes live only once MSI registers its release.
            </Typography>
          </Box>
          <Box sx={controlRowSx}>
            {LOADER_FILTERS.map((option) => (
              <Chip
                key={option.value}
                clickable
                disabled={inventoryLoading}
                aria-label={`Inventory loader ${option.label}`}
                label={option.label}
                color={inventoryLoader === option.value ? 'primary' : 'default'}
                variant={inventoryLoader === option.value ? 'filled' : 'outlined'}
                onClick={() => handleInventoryLoaderChange(option.value)}
                sx={{ fontWeight: 700 }}
              />
            ))}
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => loadInventory()}
              disabled={inventoryLoading}
              sx={refreshButtonSx}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        {inventoryError && (
          <Alert severity="error" sx={{ m: 1.2 }}>
            {inventoryError}
          </Alert>
        )}
        {inventoryReason === 'factor_data_table_inventory_missing' && (
          <Alert severity="info" sx={{ m: 1.2 }}>
            Inventory table is not available in this environment.
          </Alert>
        )}
        {inventoryMeta.hasMore && (
          <Alert severity="warning" sx={{ m: 1.2 }}>
            Showing the first {formatCount(inventoryMeta.returnedRowCount)} inventory rows.
            Narrow the loader/date filter to see a smaller result set.
          </Alert>
        )}
        {renderInventoryTable()}
      </Box>
    </Box>
  );
}

FactorDataMonitoringPanel.propTypes = {
  active: PropTypes.bool,
};

export default FactorDataMonitoringPanel;
